// La barre latérale « Mes commentaires » — l'état partagé (demande de Marc, 06/10 : « une
// gestion de commentaires un peu comme dans Google Docs […] une sorte de barre latérale, un peu
// comme un thread de leurs commentaires »).
//
// `ObsLayout` tient l'état (`useFilsEtat`) et le pose dans `FilsTerObsCtx` ; trois consommateurs
// le lisent : le bouton de l'en-tête et le tiroir (TiroirCommentaires), la bulle « Commenter » et
// la pastille d'un graphique (`Explicable`), la pastille d'une section (`SectionBlock`). SANS
// canal — visiteur, compte gratuit, équipe TerObs, drapeau `COMMENTAIRES_TEROBS` éteint, page
// partagée, vue EPCI — le contexte est nul et aucun d'eux ne rend quoi que ce soit.
//
// Ce que le serveur sait des fils, et rien de plus : un graphique déclaré a son identifiant ; une
// tuile n'en a pas de stable, son ancre est son LIBELLÉ quand le code des builders le déclare
// (#563), sinon sa section. La pastille d'une section compte tous ses fils hors graphiques (une
// section couvre ses tuiles) ; celle d'une tuile, les siens.
//
// #563 : les fils sont ceux que le compte LIT — ses notes, ses messages à TerObs, et les fils de
// son équipe —, avec les gestes que le serveur permet ; `maj` remplace un fil après un geste.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { ThemeMeta } from '../types'
import { estClos, mesMessages, NIVEAUX, pageDe, type EtatTickets, type Ancre, type Message, type Niveau } from './api'
import type { CanalTerObs } from './contexte'
import './fils.css'

/** Ce qu'on commente : l'ancre (identifiants seulement) et le titre de l'élément, pour l'œil ;
    `niveau` : le niveau imposé par le geste (« Écrire à TerObs »), sinon celui par défaut. */
export interface CibleCommentaire { ancre: Ancre; titre: string; niveau?: Niveau }

/** Sur quel(s) fil(s) ouvrir le tiroir : ceux d'un graphique, d'une tuile, ou d'une section. */
export type FocusFils = { graphique: string } | { section: string; tuile: string } | { section: string }

export interface FilsApi {
  canal: CanalTerObs
  /** La page lue, au sens des ancres : une thématique, « synthese », ou null (carte, membres…). */
  page: string | null
  ouvert: boolean
  /** Incrémenté à chaque ouverture DEMANDÉE (bouton, bulle, pastille) : le tiroir y prend le
      focus — jamais à la restauration de l'état mémorisé au chargement. */
  geste: number
  etat: EtatTickets
  filtrerEtat: (etat: EtatTickets) => void
  nonLus: number
  filsPage: Message[]
  /** Curseur des fils de la page au-delà du plafond chargé d'office (null : tout est là). */
  suivantPage: string | null
  tous: Message[] | null
  suivant: string | null
  erreur: string | null
  brouillon: CibleCommentaire | null
  enAvant: string[]
  annonce: string | null
  ancreLisible: (c: CibleCommentaire) => string
  ouvrir: (focus?: FocusFils | null, declencheur?: HTMLElement | null) => void
  fermer: () => void
  basculer: (declencheur?: HTMLElement | null) => void
  commenter: (cible: CibleCommentaire, declencheur?: HTMLElement | null) => void
  abandonner: () => void
  envoye: (m: Message) => void
  plusAnciens: () => void
  plusAnciensPage: () => void
  allerA: (m: Message) => void
  /** Un fil a changé après un geste (réponse, modification, retrait) ; `null` : il a disparu. */
  maj: (fil: Message | null, id: string) => void
  filsDuGraphique: (graphique: string) => Message[]
  filsDeTuile: (section: string | undefined, tuile: string) => Message[]
  filsDeSection: (section: string) => Message[]
  /** La page « Commentaires et échanges » transmet le compteur restant après lecture. */
  lus: (restant: number) => void
}

export const FilsTerObsCtx = createContext<FilsApi | null>(null)
export const useFilsTerObs = () => useContext(FilsTerObsCtx)

/** L'ancre d'un élément, depuis le canal (page ou section), l'identifiant du graphique ou le
    libellé de la tuile (#563). Null hors canal ou sans ancre connue (carte, page partagée) :
    rien à commenter là. Un graphique qui n'appartient pas à la section du canal n'est pas
    envoyé — le message retombe sur la section, comme le serveur le ferait. Un graphique n'est
    pas une tuile : s'il y a les deux, le graphique l'emporte. */
export function ancreDe(canal: CanalTerObs | null, graphique?: string, tuile?: string): Ancre | null {
  if (!canal || !(canal.page || canal.section)) return null
  const avecGraphique = !!graphique && (!canal.section || graphique.startsWith(`${canal.section}.`))
  return {
    ...(canal.page ? { page: canal.page } : {}),
    ...(canal.section ? { section: canal.section } : {}),
    ...(avecGraphique ? { graphique } : tuile ? { tuile } : {}),
  }
}

/** L'élément d'une ancre sur la page affichée : le graphique (`data-graphique`), sinon la tuile
    (`data-tuile`, cherchée dans sa section d'abord), sinon la section. Comparaison exacte des
    attributs — aucun libellé n'entre dans un sélecteur CSS. */
export function elementDeLAncre(a: { graphique?: string | null; tuile?: string | null;
                                     section?: string | null }): HTMLElement | null {
  const parAttribut = (attr: string, valeur: string, dans: ParentNode = document) =>
    Array.from(dans.querySelectorAll<HTMLElement>(`[${attr}]`)).find(e => e.getAttribute(attr) === valeur)
  const section = a.section ? document.getElementById(a.section) : null
  return (a.graphique ? parAttribut('data-graphique', a.graphique) : undefined)
    || (a.tuile ? (section && parAttribut('data-tuile', a.tuile, section)) || parAttribut('data-tuile', a.tuile) : undefined)
    || section
}

/** Le lien profond d'un commentaire (#563, `lienMessage`) : `?graphique=<id>` ou `?tuile=<libellé>`,
    et la section en ancre. Une fois la page chargée, fait défiler jusqu'à l'élément précis et
    l'éclaire ; rend `false` sans paramètre ou sans élément (la page retombe sur l'ancre). */
export function montrerLienProfond(search: string, hash: string): boolean {
  const q = new URLSearchParams(search)
  const graphique = q.get('graphique')
  const tuile = q.get('tuile')
  if (!graphique && !tuile) return false
  let section: string | null = null
  try { section = hash ? decodeURIComponent(hash.slice(1)) : null } catch { section = null }
  // La tuile se cherche dans sa section d'abord (deux sections peuvent porter le même libellé) ;
  // introuvable (libellé changé, graphique retiré), l'élément éclairé est la section.
  const precis = elementDeLAncre({ graphique, tuile, section })
  if (!precis) return false
  mettreEnEvidence(precis)
  return true
}

/** Fait défiler jusqu'à l'élément et l'éclaire un instant (« montrer », lien profond). */
export function mettreEnEvidence(cible: HTMLElement) {
  cible.scrollIntoView({ behavior: reduireMouvement() ? 'auto' : 'smooth', block: 'center' })
  cible.classList.remove('obs2-en-evidence')
  void cible.offsetWidth                          // relance l'animation si on reclique
  cible.classList.add('obs2-en-evidence')
  window.setTimeout(() => cible.classList.remove('obs2-en-evidence'), 2400)
}

export const estTelephone = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(max-width: 640px)').matches

const reduireMouvement = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** La page des ancres, lue dans l'adresse : `/o/<insee>/t/<thème>` → le thème ; la vue
    d'ensemble → « synthese » ; tout le reste (carte, membres, échanges…) → null. */
export function pageDuChemin(chemin: string, themes?: ThemeMeta[]): string | null {
  const t = chemin.match(/^\/o\/[^/]+\/t\/([^/#?]+)/)
  if (t) return !themes || themes.some(x => x.id === t[1]) ? t[1] : null
  if (/^\/o\/[^/]+\/?(synthese\/?)?$/.test(chemin)) return 'synthese'
  return null
}

const CLE_MEMO = 'obs2-commentaires-ouvert:'
function lireMemo(compte: string | null): boolean {
  if (!compte || estTelephone()) return false        // une feuille plein écran ne se rouvre pas seule
  try { return localStorage.getItem(CLE_MEMO + compte) === '1' } catch { return false }
}
function ecrireMemo(compte: string | null, ouvert: boolean) {
  if (!compte) return
  try { localStorage.setItem(CLE_MEMO + compte, ouvert ? '1' : '') } catch { /* navigation privée */ }
}

const PAGES_MAX = 4          // 4 × 50 fils sur UNE page : au-delà, les plus anciens n'ont pas de pastille

interface Lectures {
  file: Promise<void>
  controleur: AbortController
  reservees: Map<string, string | null>
}

/** Les fils réellement rendus dans le second groupe du tiroir, sans répéter ceux de la page.
    A20 : la page stockée reste le repli quand le serveur ne sert pas encore l'ancre actuelle. */
export function filsAilleurs(page: string | null, etat: EtatTickets, filsPage: Message[], tous: Message[]) {
  const surCettePage = new Set(filsPage.map(m => m.id))
  return tous.filter(m => etat === 'clos' || !page || (m.ancre.page !== page && !surCettePage.has(m.id)))
}

export function useFilsEtat(canal: CanalTerObs | null, themes: ThemeMeta[] | undefined,
                            compte: string | null): FilsApi | null {
  const { pathname } = useLocation()
  const page = canal ? pageDuChemin(pathname, themes) : null
  const [ouvert, setOuvert] = useState(() => !!canal && lireMemo(compte))
  const [geste, setGeste] = useState(0)
  const [etat, filtrerEtat] = useState<EtatTickets>('ouverts')
  const etatRef = useRef(etat)
  etatRef.current = etat
  const [nonLus, setNonLus] = useState(0)
  const [filsPage, setFilsPage] = useState<Message[]>([])
  const [tous, setTous] = useState<Message[] | null>(null)
  const [suivant, setSuivant] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [brouillon, setBrouillon] = useState<CibleCommentaire | null>(null)
  const [enAvant, setEnAvant] = useState<string[]>([])
  const [annonce, setAnnonce] = useState<string | null>(null)
  const [suivantPage, setSuivantPage] = useState<string | null>(null)
  const declencheur = useRef<HTMLElement | null>(null)
  const ouvertRef = useRef(ouvert)
  ouvertRef.current = ouvert
  const insee = canal?.insee
  const token = canal?.token ?? null
  // Le contexte des données : une réponse arrivée pour un AUTRE compte ou une autre commune
  // n'écrit plus rien (revue codex : la barre ne doit jamais montrer les fils d'un autre compte).
  const cle = JSON.stringify([compte, insee, token])
  const cleRef = useRef(cle)
  cleRef.current = cle
  const pageRef = useRef(page)
  pageRef.current = page
  const lectures = useRef<Lectures | null>(null)
  const pagination = useRef(new Set<string>())

  // L'état mémorisé est celui du COMPTE : à un changement de compte (ou de canal), on le relit.
  useEffect(() => { setOuvert(!!canal && lireMemo(compte)) }, [compte, !!canal])  // eslint-disable-line react-hooks/exhaustive-deps

  // Changement de compte ou de commune (ObsLayout reste monté d'une commune à l'autre) : on vide
  // TOUT avant de recharger — rien de l'ancien contexte ne reste affiché, même si la requête échoue.
  // #678 : la remise à zéro se fait PENDANT le rendu où la clé change (motif React « ajuster
  // l'état quand une prop change »), plus dans un effet. Un effet passe APRÈS le commit qui
  // affiche le canal : un geste fait entre les deux — « Commenter » cliqué dès que la bulle
  // paraît — était effacé par lui (le brouillon disparaissait, tiroir ouvert et vide), et
  // l'ancien contexte restait affiché un commit de trop.
  const [cleVue, setCleVue] = useState(cle)
  if (cleVue !== cle) {
    setCleVue(cle)
    filtrerEtat('ouverts'); setFilsPage([]); setSuivantPage(null); setTous(null); setSuivant(null); setNonLus(0)
    setErreur(null); setBrouillon(null); setEnAvant([]); setAnnonce(null)
  }
  const [pageVue, setPageVue] = useState(page)
  if (pageVue !== page) {
    setPageVue(page)
    setFilsPage([]); setSuivantPage(null)
  }
  const [etatVue, setEtatVue] = useState(etat)
  if (etatVue !== etat) {
    setEtatVue(etat)
    setTous(null); setSuivant(null)
  }

  // Les fils de la page (pour les pastilles et « Sur cette page ») et le nombre de réponses non
  // lues (pour le bouton) : une requête à chaque changement de page.
  useEffect(() => {
    setFilsPage([]); setSuivantPage(null)
    if (!insee) { setNonLus(0); return }
    let vivant = true
    ;(async () => {
      try {
        let d = await mesMessages(token, insee, false, undefined, page ?? undefined)
        if (!vivant) return
        // Une réponse partie avant le marquage du tiroir ne rétablit pas un ancien compteur.
        if (!ouvertRef.current) setNonLus(d.non_lus)
        if (!page) return
        const liste = [...d.messages]
        for (let i = 1; i < PAGES_MAX && d.suivant; i++) {
          d = await mesMessages(token, insee, false, d.suivant, page)
          if (!vivant) return
          liste.push(...d.messages)
        }
        setFilsPage(liste)
        // Au-delà du plafond, les plus anciens restent atteignables depuis la barre (revue codex).
        setSuivantPage(d.suivant)
      } catch {
        /* repères seulement : une erreur ne gêne pas la lecture de la page */
      }
    })()
    return () => { vivant = false }
  }, [insee, token, compte, page])

  // Charger les échanges ne les marque pas encore : certains sont masqués par les groupes.
  useEffect(() => {
    if (!insee || !ouvert) return
    let vivant = true
    setTous(null); setSuivant(null)
    mesMessages(token, insee, false, undefined, undefined, undefined, etat)
      .then(d => {
        if (!vivant) return
        setTous(d.messages); setSuivant(d.suivant); setErreur(null)
        if (!d.messages.length) setNonLus(d.non_lus)
      })
      .catch(e => { if (vivant) setErreur(e instanceof Error ? e.message : String(e)) })
    return () => { vivant = false }
  }, [insee, token, compte, ouvert, etat])

  // Une file par contexte : une requête suspendue ailleurs ne retarde jamais la nouvelle vue.
  // L'identité de la file protège aussi contre une réponse tardive après un aller-retour A/B/A.
  useEffect(() => {
    const contexte: Lectures = { file: Promise.resolve(), controleur: new AbortController(), reservees: new Map() }
    lectures.current = contexte
    return () => {
      lectures.current = null
      contexte.controleur.abort()
    }
  }, [cle, page, etat, ouvert])

  // #875 : lire le contenu APRÈS son affichage. Le résultat ne remplace aucune liste :
  // les champs et leurs brouillons restent montés, même si la lecture ou la pagination tarde.
  // La même sélection sert au rendu ; les repères seuls et les fils hors plafond restent non lus.
  const affiches = JSON.stringify([
    ...(page && etat === 'ouverts' ? filsPage : []),
    ...filsAilleurs(page, etat, filsPage, tous ?? []),
  ].map(m => [m.id, m.reponses.at(-1)?.id ?? null]))
  useEffect(() => {
    const contexte = lectures.current
    if (!insee || !ouvert || !contexte) return
    const rendus = JSON.parse(affiches) as [string, string | null][]
    const nouveaux = rendus.filter(([id, borne]) =>
      !contexte.reservees.has(id) || contexte.reservees.get(id) !== borne)
    nouveaux.forEach(([id, borne]) => contexte.reservees.set(id, borne))
    // La pagination ne relit que les nouveaux fils ; une réponse nouvellement rendue fait
    // avancer sa borne. Les demandes déjà en attente sont elles aussi réservées.
    contexte.file = contexte.file.then(async () => {
      for (let i = 0; i < nouveaux.length; i += 50) {
        if (contexte.controleur.signal.aborted) return
        const lot = nouveaux.slice(i, i + 50)
        try {
          const d = await mesMessages(token, insee, true, undefined, undefined, undefined, etat,
            lot.map(([id]) => id), lot.flatMap(([, borne]) => borne ? [borne] : []), contexte.controleur.signal)
          if (lectures.current === contexte) setNonLus(d.non_lus)
        } catch (e) {
          lot.forEach(([id, borne]) => {
            if (contexte.reservees.get(id) === borne) contexte.reservees.delete(id)
          })
          if (lectures.current === contexte) setErreur(e instanceof Error ? e.message : String(e))
        }
      }
    })
  }, [insee, token, cle, ouvert, etat, page, affiches])

  const changer = useCallback((v: boolean) => {
    setOuvert(v); ecrireMemo(compte, v)
  }, [compte])

  const ouvrir = useCallback((focus?: FocusFils | null, el?: HTMLElement | null) => {
    declencheur.current = el ?? null
    if (focus) filtrerEtat('ouverts')
    setEnAvant(!focus ? [] : filsPage
      .filter(m => 'graphique' in focus ? m.ancre.graphique === focus.graphique
        : 'tuile' in focus ? m.ancre.tuile === focus.tuile && m.ancre.section === focus.section
        : m.ancre.section === focus.section && !m.ancre.graphique)
      .map(m => m.id))
    setAnnonce(null)
    setGeste(g => g + 1)
    changer(true)
  }, [filsPage, changer])

  const fermer = useCallback(() => {
    changer(false)
    setEnAvant([])
    // Le focus revient d'où l'on vient — sinon au bouton de l'en-tête, jamais en haut du document.
    const retour = declencheur.current && document.contains(declencheur.current)
      ? declencheur.current : document.getElementById('obs2-fils-bouton')
    retour?.focus()
    declencheur.current = null
  }, [changer])

  const basculer = useCallback((el?: HTMLElement | null) => {
    if (ouvertRef.current) fermer()
    else ouvrir(null, el)
  }, [fermer, ouvrir])

  const commenter = useCallback((cible: CibleCommentaire, el?: HTMLElement | null) => {
    declencheur.current = el ?? null
    setBrouillon(cible)
    setEnAvant([])
    setAnnonce(null)
    setGeste(g => g + 1)
    changer(true)
  }, [changer])

  const envoye = useCallback((m: Message) => {
    filtrerEtat('ouverts')
    setBrouillon(null)
    setTous(t => t ? [m, ...t.filter(x => x.id !== m.id)] : t)
    if (pageDe(m) === page) setFilsPage(f => [m, ...f.filter(x => x.id !== m.id)])
    setEnAvant([m.id])
    setAnnonce(NIVEAUX[m.niveau ?? 'terobs'].envoye)
  }, [page])

  const maj = useCallback((fil: Message | null, id: string) => {
    const remplacer = (liste: Message[], clos: boolean) => fil && estClos(fil) === clos
      ? liste.some(x => x.id === id) ? liste.map(x => (x.id === id ? fil : x)) : [fil, ...liste]
      : liste.filter(x => x.id !== id)
    setTous(t => t && remplacer(t, etat === 'clos'))
    // Une relance depuis la pleine page rejoint aussi les repères si son ancre est ici.
    setFilsPage(f => fil && pageDe(fil) === page ? remplacer(f, false) : f.filter(x => x.id !== id))
  }, [etat, page])

  const plusAnciens = useCallback(() => {
    if (!insee || !suivant || !ouvertRef.current) return
    const avant = cle
    const requete = `${cle}|${etat}|${suivant}`
    if (pagination.current.has(requete)) return
    pagination.current.add(requete)
    mesMessages(token, insee, false, suivant, undefined, undefined, etat)
      .then(d => {
        if (cleRef.current !== avant || etatRef.current !== etat) return
        setTous(t => [...(t ?? []), ...d.messages.filter(m => !t?.some(f => f.id === m.id))])
        setSuivant(d.suivant)
      })
      .catch(e => { if (cleRef.current === avant && etatRef.current === etat) setErreur(e instanceof Error ? e.message : String(e)) })
      .finally(() => { pagination.current.delete(requete) })
  }, [insee, token, suivant, cle, etat])

  const plusAnciensPage = useCallback(() => {
    if (!insee || !suivantPage || !page || !ouvertRef.current || etatRef.current !== 'ouverts') return
    const avant = cle
    const requete = `${cle}|page:${page}|${suivantPage}`
    if (pagination.current.has(requete)) return
    pagination.current.add(requete)
    mesMessages(token, insee, false, suivantPage, page)
      .then(d => {
        if (cleRef.current !== avant || pageRef.current !== page) return
        setFilsPage(f => [...f, ...d.messages.filter(m => !f.some(x => x.id === m.id))]); setSuivantPage(d.suivant)
      })
      .catch(e => { if (cleRef.current === avant) setErreur(e instanceof Error ? e.message : String(e)) })
      .finally(() => { pagination.current.delete(requete) })
  }, [insee, token, suivantPage, page, cle])

  const allerA = useCallback((m: Message) => {
    const cible = elementDeLAncre({ graphique: m.ancre.graphique, tuile: m.ancre.tuile,
                                    section: m.section_actuelle || m.ancre.section })
      || document.getElementById('main-content')
    if (!cible) return
    // Sur téléphone, le tiroir couvre la page : on le replie pour montrer l'élément.
    if (estTelephone()) { changer(false); setEnAvant([]) }
    mettreEnEvidence(cible)
  }, [changer])

  const parGraphique = useMemo(() => {
    const m = new Map<string, Message[]>()
    for (const f of filsPage) if (f.ancre.graphique) m.set(f.ancre.graphique, [...(m.get(f.ancre.graphique) ?? []), f])
    return m
  }, [filsPage])
  const parTuile = useMemo(() => {
    const m = new Map<string, Message[]>()
    for (const f of filsPage) {
      if (f.ancre.tuile && !f.ancre.graphique) {
        const k = `${f.ancre.section ?? ''}|${f.ancre.tuile}`
        m.set(k, [...(m.get(k) ?? []), f])
      }
    }
    return m
  }, [filsPage])
  const parSection = useMemo(() => {
    const m = new Map<string, Message[]>()
    for (const f of filsPage) {
      if (f.ancre.section && !f.ancre.graphique) m.set(f.ancre.section, [...(m.get(f.ancre.section) ?? []), f])
    }
    return m
  }, [filsPage])

  const ancreLisible = useCallback((c: CibleCommentaire) => {
    const { page: p, section, graphique } = c.ancre
    const sec = section ?? graphique?.split('.', 1)[0]
    const theme = themes?.find(t => (sec && t.sections.some(s => s.id === sec)) || t.id === p)
    const titreSection = sec ? theme?.sections.find(s => s.id === sec)?.title : undefined
    const titrePage = p === 'synthese' ? 'Synthèse' : theme?.label
    return [titrePage, titreSection, c.titre !== titreSection ? c.titre : null]
      .filter(Boolean).join(' › ')
  }, [themes])

  // Stables : la page « Échanges » s'en sert dans la dépendance d'un effet de chargement.
  const lus = useCallback((restant: number) => setNonLus(restant), [])
  const abandonner = useCallback(() => setBrouillon(null), [])

  return useMemo(() => canal ? {
    canal, page, ouvert, geste, etat, filtrerEtat, nonLus, filsPage, suivantPage, tous, suivant, erreur, brouillon,
    enAvant, annonce, ancreLisible, ouvrir, fermer, basculer, commenter, abandonner, envoye,
    plusAnciens, plusAnciensPage, allerA, maj,
    filsDuGraphique: (g: string) => parGraphique.get(g) ?? [],
    filsDeTuile: (s: string | undefined, t: string) => parTuile.get(`${s ?? ''}|${t}`) ?? [],
    filsDeSection: (s: string) => parSection.get(s) ?? [],
    lus,
  } : null, [canal, page, ouvert, geste, etat, filtrerEtat, nonLus, filsPage, suivantPage, tous, suivant, erreur,
             brouillon, enAvant, annonce, ancreLisible, ouvrir, fermer, basculer, commenter,
             abandonner, envoye, plusAnciens, plusAnciensPage, allerA, maj, parGraphique, parTuile,
             parSection, lus])
}
